/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

// Convert ISO datetime → yyyy-MM-ddTHH:mm for datetime-local input
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function fromLocalInput(local) {
  if (!local) return null;
  try { return new Date(local).toISOString(); } catch { return null; }
}

export default function AdminFeatured() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // {entity_type, entity_id, ...current values}

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/featured"),
        api.get("/admin/featured/analytics"),
      ]);
      setItems(r1.data || []);
      setAnalytics(r2.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load featured");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.entity?.name || it.entity_id}" from featured?`)) return;
    try {
      await api.delete(`/admin/featured/${it.entity_type}/${it.entity_id}`);
      toast.success("Removed from featured");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const expiredCount = items.filter(i => i.expired).length;

  return (
    <div className="m-admin-section" data-testid="admin-featured">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <span className="m-eyebrow">Admin · Curated placements</span>
          <h2 className="m-h2" style={{ margin: "4px 0 0" }}>Featured workspaces.</h2>
          <p className="m-muted small" style={{ marginTop: 6 }}>
            Pin venues or child workspaces to the top of <code>/spaces</code>, <code>/workspaces</code>, and the Featured carousel. Sorted by priority (lower = shown first).
          </p>
        </div>
        <button className="m-btn-gold" onClick={() => setShowAdd(true)} data-testid="admin-featured-add-btn">+ Add featured</button>
      </div>

      {expiredCount > 0 && (
        <div className="m-empty" data-testid="admin-featured-expired-banner"
             style={{ background: "rgba(184,145,58,0.1)", borderColor: "var(--gold)", color: "var(--m-text)" }}>
          ⚠️ {expiredCount} featured entry{expiredCount === 1 ? "" : "'s"} end date has passed. They're still shown to visitors — remove them below or extend the end date.
        </div>
      )}

      {loading ? (
        <div className="m-loading-inline">Loading…</div>
      ) : items.length === 0 ? (
        <div className="m-empty" data-testid="admin-featured-empty">Nothing featured right now. Click "+ Add featured" to promote a workspace or venue.</div>
      ) : (
        <div className="m-card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(27,35,64,0.12)", textAlign: "left" }}>
                <th style={{ padding: "12px 14px" }}>#</th>
                <th style={{ padding: "12px 14px" }}>Entity</th>
                <th style={{ padding: "12px 14px" }}>Type</th>
                <th style={{ padding: "12px 14px" }}>Badge</th>
                <th style={{ padding: "12px 14px" }}>Window</th>
                <th style={{ padding: "12px 14px", textAlign: "right" }}>Impressions</th>
                <th style={{ padding: "12px 14px", textAlign: "right" }}>Clicks</th>
                <th style={{ padding: "12px 14px", textAlign: "right" }}>CTR</th>
                <th style={{ padding: "12px 14px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.entity_type}:${it.entity_id}`}
                    data-testid={`admin-featured-row-${it.entity_id}`}
                    style={{ borderBottom: "1px solid rgba(27,35,64,0.06)", opacity: it.expired ? 0.6 : 1 }}>
                  <td style={{ padding: "10px 14px", fontWeight: 700 }}>{it.priority}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ fontWeight: 600 }}>{it.entity?.name || <em style={{ color: "#b04040" }}>Orphaned: {it.entity_id}</em>}</div>
                    <div className="m-muted small">
                      {[it.entity?.neighborhood, it.entity?.city].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", textTransform: "uppercase", fontSize: 11, letterSpacing: 0.14 }}>
                    {it.entity_type}
                  </td>
                  <td style={{ padding: "10px 14px" }}>{it.badge_label}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12 }}>
                    {it.starts_at ? <div>From {fmtDate(it.starts_at)}</div> : <div className="m-muted">Immediate</div>}
                    {it.ends_at ? (
                      <div style={{ color: it.expired ? "#b04040" : "inherit" }}>
                        {it.expired ? "Ended" : "Until"} {fmtDate(it.ends_at)}
                      </div>
                    ) : <div className="m-muted">No end date</div>}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                      data-testid={`admin-featured-impressions-${it.entity_id}`}>
                    {it.analytics?.impressions || 0}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                      data-testid={`admin-featured-clicks-${it.entity_id}`}>
                    {it.analytics?.clicks || 0}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {(it.analytics?.ctr * 100 || 0).toFixed(1)}%
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="m-btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }}
                        onClick={() => setEditing(it)}
                        data-testid={`admin-featured-edit-${it.entity_id}`}>Edit</button>
                      <button className="m-btn-ghost" style={{ padding: "4px 10px", fontSize: 12, color: "#b04040" }}
                        onClick={() => remove(it)}
                        data-testid={`admin-featured-remove-${it.entity_id}`}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && analytics.length > items.length && (
        <div style={{ marginTop: 28 }}>
          <h3 className="m-side-title">Historical performance</h3>
          <p className="m-muted small">Entities that were previously featured but aren't right now. Data is retained so you can decide who to re-promote.</p>
          <div className="m-card" style={{ padding: 0, overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(27,35,64,0.12)", textAlign: "left" }}>
                  <th style={{ padding: "10px 14px" }}>Entity</th>
                  <th style={{ padding: "10px 14px" }}>Type</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Impressions</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>Clicks</th>
                  <th style={{ padding: "10px 14px", textAlign: "right" }}>CTR</th>
                </tr>
              </thead>
              <tbody>
                {analytics.filter(a => !a.currently_featured).map((a) => (
                  <tr key={`${a.entity_type}:${a.entity_id}`}
                      style={{ borderBottom: "1px solid rgba(27,35,64,0.06)" }}>
                    <td style={{ padding: "8px 14px" }}>{a.entity_name || a.entity_id}</td>
                    <td style={{ padding: "8px 14px", fontSize: 11, textTransform: "uppercase" }}>{a.entity_type}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.impressions}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{a.clicks}</td>
                    <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{(a.ctr * 100 || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && <AddFeaturedModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <EditFeaturedModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AddFeaturedModal({ onClose, onSaved }) {
  const [entityType, setEntityType] = useState("workspace");
  const [choices, setChoices] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [priority, setPriority] = useState(100);
  const [badge, setBadge] = useState("Featured");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const url = entityType === "workspace" ? "/spaces" : "/locations";
    api.get(url).then(r => {
      // /spaces returns cards. /locations returns { locations: [...] } — normalize.
      const raw = r.data;
      const list = Array.isArray(raw) ? raw : (raw.locations || raw.items || []);
      setChoices(list.map(x => ({
        id: entityType === "workspace" ? (x.workspace_id || x.slug) : (x.location_id || x.value || x.slug),
        name: x.name || x.label,
        meta: entityType === "workspace" ? `${x.city || ""} · ${x.type || ""}` : (x.city || x.state || ""),
      })).filter(x => x.id && x.name));
    }).catch(() => setChoices([]));
  }, [entityType]);

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) { toast.error("Pick an entity to feature"); return; }
    setSaving(true);
    try {
      await api.post("/admin/featured", {
        entity_type: entityType,
        entity_id: entityId,
        priority: Number(priority),
        badge_label: badge,
        starts_at: startsAt ? fromLocalInput(startsAt) : null,
        ends_at: endsAt ? fromLocalInput(endsAt) : null,
        note: note || null,
      });
      toast.success("Featured");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="m-welcome-backdrop" role="dialog" aria-modal="true"
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="admin-featured-add-modal">
      <div className="m-welcome-card" style={{ maxWidth: 520 }}>
        <button type="button" className="m-welcome-close" onClick={onClose} aria-label="Close">×</button>
        <div style={{ padding: "8px 32px 24px" }}>
          <span className="m-eyebrow">Add featured</span>
          <h2 className="m-h2" style={{ marginTop: 4 }}>Promote a listing.</h2>
          <form className="m-form" onSubmit={submit} style={{ marginTop: 12 }}>
            <label className="m-field"><span>Entity type</span>
              <select value={entityType} onChange={e => { setEntityType(e.target.value); setEntityId(""); }}
                data-testid="admin-featured-entity-type">
                <option value="workspace">Workspace (child)</option>
                <option value="venue">Venue (parent — features all children)</option>
              </select>
            </label>
            <label className="m-field"><span>{entityType === "workspace" ? "Workspace" : "Venue"}</span>
              <select value={entityId} onChange={e => setEntityId(e.target.value)}
                required data-testid="admin-featured-entity-id">
                <option value="">— Choose —</option>
                {choices.map(c => <option key={c.id} value={c.id}>{c.name} ({c.meta})</option>)}
              </select>
            </label>
            <div className="m-form-grid-2">
              <label className="m-field"><span>Priority (1 = top)</span>
                <input type="number" min={1} max={9999} value={priority}
                  onChange={e => setPriority(e.target.value)} data-testid="admin-featured-priority" />
              </label>
              <label className="m-field"><span>Badge label</span>
                <input value={badge} onChange={e => setBadge(e.target.value)} maxLength={32}
                  data-testid="admin-featured-badge" />
              </label>
            </div>
            <div className="m-form-grid-2">
              <label className="m-field"><span>Starts at (optional)</span>
                <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)}
                  data-testid="admin-featured-starts" />
              </label>
              <label className="m-field"><span>Ends at (optional)</span>
                <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)}
                  data-testid="admin-featured-ends" />
              </label>
            </div>
            <label className="m-field"><span>Note (admin only)</span>
              <input value={note} onChange={e => setNote(e.target.value)} maxLength={500}
                data-testid="admin-featured-note" placeholder="Why is this featured?" />
            </label>
            <div className="m-form-actions" style={{ marginTop: 14 }}>
              <button type="button" className="m-btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="m-btn-gold" disabled={saving}
                data-testid="admin-featured-save">{saving ? "Saving…" : "Save featured"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditFeaturedModal({ item, onClose, onSaved }) {
  const [priority, setPriority] = useState(item.priority || 100);
  const [badge, setBadge] = useState(item.badge_label || "Featured");
  const [startsAt, setStartsAt] = useState(toLocalInput(item.starts_at));
  const [endsAt, setEndsAt] = useState(toLocalInput(item.ends_at));
  const [note, setNote] = useState(item.note || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/featured/${item.entity_type}/${item.entity_id}`, {
        priority: Number(priority),
        badge_label: badge,
        starts_at: startsAt ? fromLocalInput(startsAt) : null,
        ends_at: endsAt ? fromLocalInput(endsAt) : null,
        note: note || null,
        clear_starts_at: !startsAt,
        clear_ends_at: !endsAt,
      });
      toast.success("Updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally { setSaving(false); }
  };

  return (
    <div className="m-welcome-backdrop" role="dialog" aria-modal="true"
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="admin-featured-edit-modal">
      <div className="m-welcome-card" style={{ maxWidth: 520 }}>
        <button type="button" className="m-welcome-close" onClick={onClose} aria-label="Close">×</button>
        <div style={{ padding: "8px 32px 24px" }}>
          <span className="m-eyebrow">Edit featured</span>
          <h2 className="m-h2" style={{ marginTop: 4 }}>{item.entity?.name || item.entity_id}</h2>
          <form className="m-form" onSubmit={submit} style={{ marginTop: 12 }}>
            <div className="m-form-grid-2">
              <label className="m-field"><span>Priority</span>
                <input type="number" min={1} max={9999} value={priority}
                  onChange={e => setPriority(e.target.value)} />
              </label>
              <label className="m-field"><span>Badge label</span>
                <input value={badge} onChange={e => setBadge(e.target.value)} maxLength={32} />
              </label>
            </div>
            <div className="m-form-grid-2">
              <label className="m-field"><span>Starts at</span>
                <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
              </label>
              <label className="m-field"><span>Ends at</span>
                <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
              </label>
            </div>
            <label className="m-field"><span>Note</span>
              <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} />
            </label>
            <div className="m-form-actions" style={{ marginTop: 14 }}>
              <button type="button" className="m-btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="m-btn-gold" disabled={saving}
                data-testid="admin-featured-update">{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
